import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useAuth0 } from '@auth0/auth0-react'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import TableWidgetSeparated from '@modul-connect/shared/components/molecules/tableWidget/tableWidgetSeparated.v2'
import { dateTimeToString } from '@modul-connect/shared/utils/dateTimeUtils'
import { fetchIdleTimeReports } from '../../../../state/actions/vehicles'
import { getServiceList } from '../../../../utils/ServiceList'
import { ServiceType } from '@modul-connect/shared/utils/services'
import ReportPage from '../../reportPage.v2'
import { timespanOptions } from '../../datePickers/timespanPickers/timespanPickers'
import { getIdleTimeDetailColumns, getIdleTimeSummaryColumns } from './idleTimeColumns'

const durationStringToSeconds = (duration) => {
  if (!duration) return 0
  const [hours, minutes, seconds] = duration.split(':').map(Number)
  return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0)
}

const formatIdleDuration = (totalSeconds, t) => {
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  if (days > 0) {
    const dayUnit = t(days === 1 ? 'drivingJournal.idleTimeReport.durationDay' : 'drivingJournal.idleTimeReport.durationDays')
    const parts = [`${days}${dayUnit}`]
    if (hours > 0) parts.push(`${hours}${t('drivingJournal.idleTimeReport.durationHour')}`)
    if (minutes > 0) parts.push(`${minutes}${t('drivingJournal.idleTimeReport.durationMinute')}`)
    return parts.join(' ')
  }

  if (hours > 0) {
    const parts = [`${hours}${t('drivingJournal.idleTimeReport.durationHour')}`]
    if (minutes > 0) parts.push(`${minutes}${t('drivingJournal.idleTimeReport.durationMinute')}`)
    return parts.join(' ')
  }

  return `${minutes}${t('drivingJournal.idleTimeReport.durationMinute')}`
}

const groupByVehicle = (events, t) => {
  if (!events || !events.length) return []

  const byVehicle = {}
  events.forEach(event => {
    const key = event.mainboxId ?? event.vin
    if (!byVehicle[key]) byVehicle[key] = []
    byVehicle[key].push(event)
  })

  return Object.keys(byVehicle).map(key => {
    const vehicleEvents = byVehicle[key]
    const totalSeconds = vehicleEvents.reduce((sum, event) => sum + durationStringToSeconds(event.ignitionOnDuration), 0)

    return {
      id: key,
      vehicleName: vehicleEvents[0].vehicleName,
      vin: vehicleEvents[0].vin,
      eventCount: vehicleEvents.length,
      totalIdleDuration: formatIdleDuration(totalSeconds, t),
      isExpandable: true,
      collapsibleData: vehicleEvents
    }
  })
}

const prepareCollapsibleData = (events, t) => {
  if (!events || !events.length) return events

  const sortedEvents = [...events].sort((a, b) => {
    const aTime = a.ignitionOnFrom ? DateTime.fromISO(a.ignitionOnFrom).toMillis() : 0
    const bTime = b.ignitionOnFrom ? DateTime.fromISO(b.ignitionOnFrom).toMillis() : 0
    return bTime - aTime
  })

  return sortedEvents.map(event => {
    const onFromDT = event.ignitionOnFrom ? DateTime.fromISO(event.ignitionOnFrom) : null
    const onUntilDT = event.ignitionOnUntil ? DateTime.fromISO(event.ignitionOnUntil) : null

    return [
      {
        value1: onUntilDT ? dateTimeToString(onUntilDT, 'date-only') : '-',
        value2: onFromDT ? dateTimeToString(onFromDT, 'date-only') : '-'
      },
      {
        value1: onUntilDT ? dateTimeToString(onUntilDT, 'time-only') : '-',
        value2: onFromDT ? dateTimeToString(onFromDT, 'time-only') : '-'
      },
      { value: event.ignitionOnDuration ? formatIdleDuration(durationStringToSeconds(event.ignitionOnDuration), t) : '-' }
    ]
  })
}

const IdleTimeReportPage = ({
  idle_time_reports,
  loading,
  themes,
  subtrees,
  selected_organisations,
  fetchIdleTimeReports
}) => {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth0()

  const disabled = !getServiceList({ subtrees }).hasServiceForSelected(selected_organisations, ServiceType.IdleLogs)

  const [startDate, setStartDate] = useState(DateTime.local().startOf('month'))
  const [endDate, setEndDate] = useState(DateTime.local().endOf('month'))

  useEffect(() => {
    getAccessTokenSilently()
      .then(accessToken => {
        fetchIdleTimeReports(accessToken, startDate, endDate)
      })
  }, [startDate, endDate, selected_organisations])

  const tableData = groupByVehicle(idle_time_reports, t)

  return (
    <ReportPage
      title={t('drivingJournal.idleTimeReport.title')}
      timeSelectors={[
        timespanOptions.year,
        timespanOptions.month,
        timespanOptions.week,
        timespanOptions.day,
        timespanOptions.custom
      ]}
      noDataMessage={t('drivingJournal.idleTimeReport.noData')}
      startDate={startDate}
      endDate={endDate}
      onTimeframePicked={(start, end) => {
        setStartDate(start)
        setEndDate(end)
      }}
      disabled={disabled}
      noSearch
      isLoading={loading.fetchIdleTimeReports === 'loading'}
      fetchingDataFailed={loading.fetchIdleTimeReports === 'failed'}
      tables={
        <TableWidgetSeparated
          data={tableData}
          loadingStatus={
            loading.fetchIdleTimeReports === 'failed'
              ? 'failed'
              : (loading.fetchIdleTimeReports === 'loading' || loading.fetchIdleTimeReports === undefined)
                ? 'loading'
                : null
          }
          totalItems={tableData.length}
          columns={getIdleTimeSummaryColumns(t)}
          themes={themes}
          isSortable
          defaultSort="vehicleName"
          defaultSortOrder="asc"
          fixedLayout
          collapsibleLogColumns={getIdleTimeDetailColumns(t)}
          prepareCollapsibleData={(data) => prepareCollapsibleData(data, t)}
          collapsibleDefaultSortOrder={'desc'}
        />
      }
    />
  )
}

const mapStateToProps = props => props

const mapDispatchToProps = dispatch => ({
  fetchIdleTimeReports: (accessToken, start, end) => dispatch(fetchIdleTimeReports(accessToken, start, end)),
})

export default connect(mapStateToProps, mapDispatchToProps)(IdleTimeReportPage)
